import styles from "./DatePickerSection.module.scss";

import { useCallback, useMemo } from "react";

import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { AppDatePicker } from "@/modules/shared/components/AppDatePicker";
import { utcDateFromLocalFields } from "@/modules/shared/components/AppDatePicker/utils/utcDateFromLocalFields";
import { isEmptyObj } from "@/modules/shared/utils/isEmptyObj";

import { useBookingContext } from "../../context/BookingContext";
import { getLastBookableMonth } from "../../helpers/getLastBookableMonth";
import { formatTrialEndDate } from "../../utils/formatTrialEndDate";
import { parseTrialEndDate } from "../../utils/parseTrialEndDate";

type DatePickerSectionProps = {
  isLoading: boolean;
  timezonedSpots: Record<string, string[]>;
};

export const DatePickerSection = ({
  isLoading,
  timezonedSpots,
}: DatePickerSectionProps) => {
  const { t } = useTranslation();
  const {
    handleDateSelect,
    handleAvailabilityIntervalChange,
    selectedDate,
    timezone,
    isTrial,
    trialEndDate,
  } = useBookingContext();

  const isWholeMonthDisabled = useMemo(() => {
    if (isLoading) {
      return false;
    }

    return isEmptyObj(timezonedSpots);
  }, [isLoading, timezonedSpots]);

  // Memoize the parsed trial end so both the chevron clamp (trialEndMonth)
  // and the day-level clamp (isDisabledDate) share the same anchor.
  const parsedTrialEnd = useMemo(() => {
    if (!isTrial) {
      return null;
    }
    return parseTrialEndDate(trialEndDate);
  }, [isTrial, trialEndDate]);

  // YYYY-MM-DD string of the trial-end day, derived with the same formatter
  // used to key timezonedSpots, so the comparison below stays consistent.
  const trialEndDayKey = useMemo(() => {
    return parsedTrialEnd ? format(parsedTrialEnd, "yyyy-MM-dd") : null;
  }, [parsedTrialEnd]);

  const isDisabledDate = useCallback(
    (date: Date) => {
      const dateKey = format(date, "yyyy-MM-dd");

      // MEM-520: trial members cannot select days past their trial end date.
      // Matches Caladan's behavior (PR #329) so both clients clamp the same.
      if (trialEndDayKey && dateKey > trialEndDayKey) {
        return true;
      }

      if (timezonedSpots?.[dateKey]?.length) {
        return false;
      }

      return true;
    },
    [timezonedSpots, trialEndDayKey],
  );

  // Trial members always see this copy in the calendar module (MEM-520);
  // non-trial members fall through to the default calendar render.
  const trialSubtitle = useMemo(() => {
    if (!isTrial) {
      return null;
    }
    const formatted = formatTrialEndDate(trialEndDate);
    if (!formatted) {
      return null;
    }
    return t("bookingWidget.trialEndsOn", { date: formatted });
  }, [isTrial, trialEndDate, t]);

  // Anchor on the first of the trial end month so the whole trial month stays
  // reachable; the day level disable clamps the exact dates past the trial end.
  const trialEndMonth = useMemo(() => {
    if (!parsedTrialEnd) {
      return undefined;
    }
    return utcDateFromLocalFields(
      parsedTrialEnd.getFullYear(),
      parsedTrialEnd.getMonth(),
      1,
    );
  }, [parsedTrialEnd]);

  // Past this month Glepnir rejects start_date; Next is disabled so we never fetch it.
  const endMonth = useMemo(() => {
    const lastBookableMonth = getLastBookableMonth();

    if (trialEndMonth && trialEndMonth < lastBookableMonth) {
      return trialEndMonth;
    }

    return lastBookableMonth;
  }, [trialEndMonth]);

  return (
    <div className={styles.datePickerSection}>
      <div className={styles.titleContainer}>
        <span className={styles.title}>
          {t("bookingWidget.datePickerTitle")}
        </span>
      </div>
      <AppDatePicker
        defaultSelected={selectedDate ? new Date(selectedDate) : undefined}
        onSelectDate={handleDateSelect}
        onMonthChange={handleAvailabilityIntervalChange}
        isLoading={isLoading}
        isDisabledDate={isDisabledDate}
        forceDisabledMonthState={isWholeMonthDisabled}
        userTimezone={timezone?.value}
        monthSubtitle={trialSubtitle}
        endMonth={endMonth}
      />
    </div>
  );
};
